import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import toast from 'react-hot-toast';
import { api } from '@/lib/api';
import type { Spotlight, SpotlightStatus } from '@hellenica/shared';

type SpotlightFormValues = Omit<Spotlight, 'id' | 'slug' | 'created_at' | 'updated_at'>;

export default function SpotlightEdit() {
  const { id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const isNew = !id;

  const { data: spotlight, isLoading } = useQuery<Spotlight>({
    queryKey: ['spotlight', id],
    queryFn: () => api.get(`/v1/admin/spotlights/${id}`),
    enabled: !!id,
  });

  const defaultValues: SpotlightFormValues = {
    title: '',
    excerpt: null,
    content: null,
    hero_image_id: null,
    gallery_images: null,
    featured: false,
    status: 'draft' as SpotlightStatus,
    published_at: null,
    meta_title: null,
    meta_description: null,
  };

  const { register, handleSubmit } = useForm<SpotlightFormValues>({
    values: spotlight ? { ...defaultValues, ...spotlight } : defaultValues,
  });

  const saveMutation = useMutation({
    mutationFn: (data: Partial<Spotlight>) =>
      isNew ? api.post('/v1/admin/spotlights', data) : api.put(`/v1/admin/spotlights/${id}`, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['spotlights'] });
      toast.success(isNew ? 'Spotlight created' : 'Spotlight updated');
      navigate('/spotlight');
    },
  });

  if (!isNew && isLoading) return <div className="animate-pulse">Loading...</div>;

  return (
    <div className="max-w-3xl">
      <h1 className="text-2xl font-semibold text-gray-900 mb-8">{isNew ? 'New Spotlight' : 'Edit Spotlight'}</h1>
      <form onSubmit={handleSubmit((data) => saveMutation.mutate(data))} className="space-y-6">
        <div className="bg-white shadow rounded-lg p-6 space-y-6">
          <div>
            <label className="block text-sm font-medium text-gray-700">Title</label>
            <input type="text" {...register('title')} className="mt-1 block w-full rounded-md border-gray-300 shadow-sm" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">Excerpt</label>
            <textarea {...register('excerpt')} rows={2} className="mt-1 block w-full rounded-md border-gray-300 shadow-sm" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">Content</label>
            <textarea {...register('content')} rows={12} className="mt-1 block w-full rounded-md border-gray-300 shadow-sm" />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700">Status</label>
              <select {...register('status')} className="mt-1 block w-full rounded-md border-gray-300 shadow-sm">
                <option value="draft">Draft</option>
                <option value="published">Published</option>
              </select>
            </div>
            <div className="flex items-center pt-6">
              <input type="checkbox" {...register('featured')} className="h-4 w-4 text-brand-bronze border-gray-300 rounded" />
              <label className="ml-2 block text-sm text-gray-700">Featured</label>
            </div>
          </div>
        </div>
        <div className="flex justify-end gap-4">
          <button type="button" onClick={() => navigate('/spotlight')} className="px-4 py-2 border border-gray-300 rounded-md">Cancel</button>
          <button type="submit" className="px-4 py-2 bg-brand-bronze text-white rounded-md">Save</button>
        </div>
      </form>
    </div>
  );
}
